import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap } from './client';
import { errorMessage } from './errors';
import type { DataObject } from './objects';
import type { components } from './schema';

export type Run = components['schemas']['Run'];
export type RunDetail = components['schemas']['RunDetail'];
export type RunType = components['schemas']['RunType'];
export type LaunchRunBody = components['schemas']['LaunchRunRequest'];
export type RunPayloads = components['schemas']['RunPayloads'];
export type RunRefresh = components['schemas']['RunRefresh'];
export type WebhookDelivery = components['schemas']['WebhookDelivery'];
export type IntakeRunType = LaunchRunBody['type'];
export type IntakeTypes = components['schemas']['IntakeTypes'];

export const TERMINAL_RUN_STATES = new Set(['succeeded', 'failed', 'cancelled']);
export const isTerminal = (state: string | undefined) => !!state && TERMINAL_RUN_STATES.has(state);

export interface RunFilters {
  project?: string;
  object?: string;
  analysis?: string;
  /** One run state, several comma-separated, or `active` (created, pending, running). */
  state?: string;
  type?: string;
  /** Only runs that finished within this many hours of the request (sent as `finished_after`). */
  finishedWithinHours?: number;
  /** forint id, forun number (`228`, `run 228`) or external_ref. */
  q?: string;
}

export interface RunPage {
  results: Run[];
  next?: string | null;
}

/** Client-side view of a run (R13): existence and whether it is finished. */
export interface ClientRun {
  public_id: string;
  type: string;
  state_client: 'running' | 'finished';
  created_at: string;
}

export async function listRunTypes(): Promise<RunType[]> {
  return unwrap(await api.GET('/api/run-types'));
}
export async function refreshRunTypes(): Promise<{ added: string[]; changed: string[]; inactive: string[] }> {
  return unwrap(await api.POST('/api/run-types/refresh')) as { added: string[]; changed: string[]; inactive: string[] };
}
export async function listRunsPage(f: RunFilters = {}, cursor?: string): Promise<RunPage> {
  const { finishedWithinHours, ...query } = f;
  const finished_after = finishedWithinHours === undefined ? undefined : new Date(Date.now() - finishedWithinHours * 3600_000).toISOString();
  return unwrap(await api.GET('/api/runs', { params: { query: { ...query, ...(finished_after ? { finished_after } : {}), cursor } } }));
}
/** The cursor of a page's `next` link; only the cursor is reused, never the link's host. */
export function nextCursor(next: string | null | undefined): string | undefined {
  if (!next) return undefined;
  return new URL(next, window.location.origin).searchParams.get('cursor') ?? undefined;
}
export async function getRun(id: string): Promise<RunDetail> {
  return unwrap(await api.GET('/api/runs/{public_id}', { params: { path: { public_id: id } } }));
}
export async function runLogs(id: string, since?: string): Promise<string> {
  const r = await api.GET('/api/runs/{public_id}/logs', { params: { path: { public_id: id }, query: { since } }, parseAs: 'text' });
  if (!r.response.ok) throw new Error(`logs ${r.response.status}`);
  return (r.data as unknown as string) ?? '';
}
export async function runPayloads(id: string): Promise<RunPayloads> {
  return unwrap(await api.GET('/api/runs/{public_id}/payloads', { params: { path: { public_id: id } } }));
}
export async function cancelRun(id: string): Promise<Run> {
  return unwrap(await api.POST('/api/runs/{public_id}/cancel', { params: { path: { public_id: id } } }));
}
/** Polls forun for this run now; `advanced` is false when forun had nothing newer. */
export async function refreshRun(id: string): Promise<RunRefresh> {
  return unwrap(await api.POST('/api/runs/{public_id}/refresh', { params: { path: { public_id: id } } }));
}
export async function listObjectRuns(objectId: string): Promise<Run[]> {
  return unwrap(await api.GET('/api/objects/{public_id}/runs', { params: { path: { public_id: objectId } } }));
}
/** The run type each intake role resolves to for this object; forint never hardcodes forun's keys. */
export async function listIntakeTypes(objectId: string): Promise<IntakeTypes> {
  return unwrap(await api.GET('/api/objects/{public_id}/intake-types', { params: { path: { public_id: objectId } } }));
}
export async function markReady(objectId: string): Promise<DataObject> {
  return unwrap(await api.POST('/api/objects/{public_id}/mark-ready', { params: { path: { public_id: objectId } } }));
}
export async function launchRun(objectId: string, body: LaunchRunBody): Promise<Run> {
  return unwrap(await api.POST('/api/objects/{public_id}/runs', { params: { path: { public_id: objectId } }, body }));
}

export function useRunTypes() {
  return useQuery({ queryKey: ['run-types'], queryFn: listRunTypes, staleTime: 60_000 });
}
export function useRefreshRunTypes() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: refreshRunTypes, onSuccess: () => void qc.invalidateQueries({ queryKey: ['run-types'] }) });
}
/**
 * Runs newest first, 50 at a time. Only the first page is polled (every 5 s while `live`);
 * older pages load on `loadMore` and are not polled. When the first older page is asked for,
 * the first page as it stood is kept too, so runs pushed off it by newer ones do not vanish.
 * Remount (e.g. `key` on the filters) to start over.
 */
export function useRuns(f: RunFilters = {}, live = true) {
  const first = useQuery({ queryKey: ['runs', f, 'first'], queryFn: () => listRunsPage(f), refetchInterval: live ? 5000 : false });
  const [older, setOlder] = useState<{ from: string; snapshot: Run[] } | null>(null);
  const pages = useInfiniteQuery({
    queryKey: ['runs', f, 'older', older?.from],
    queryFn: ({ pageParam }) => listRunsPage(f, pageParam),
    initialPageParam: older?.from ?? '',
    getNextPageParam: (last) => nextCursor(last.next),
    enabled: !!older,
    staleTime: Infinity,
  });
  const seen = new Set<string>();
  const rows: Run[] = [];
  for (const r of [...(first.data?.results ?? []), ...(older?.snapshot ?? []), ...(pages.data?.pages.flatMap((p) => p.results) ?? [])]) {
    if (seen.has(r.public_id)) continue;
    seen.add(r.public_id);
    rows.push(r);
  }
  const hasMore = older ? pages.hasNextPage || pages.isPending : !!nextCursor(first.data?.next);
  const loadMore = () => {
    if (!older) {
      const from = nextCursor(first.data?.next);
      if (from) setOlder({ from, snapshot: first.data?.results ?? [] });
    } else if (pages.hasNextPage) void pages.fetchNextPage();
  };
  return {
    data: first.data ? rows : undefined,
    isPending: first.isPending,
    isError: first.isError,
    error: first.error,
    hasMore,
    loadingMore: pages.isFetching,
    loadMore,
  };
}
export function useRun(id: string | undefined) {
  return useQuery({
    queryKey: ['run', id],
    queryFn: () => getRun(id!),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data && isTerminal(q.state.data.state) ? false : 3000),
  });
}
export function useRunLogs(id: string | undefined, active: boolean) {
  return useQuery({ queryKey: ['run-logs', id], queryFn: () => runLogs(id!), enabled: !!id, refetchInterval: active ? 5000 : false });
}
export function useRunPayloads(id: string | undefined) {
  return useQuery({ queryKey: ['run-payloads', id], queryFn: () => runPayloads(id!), enabled: !!id });
}
export function useObjectRuns(objectId: string | undefined, live = true) {
  return useQuery({
    queryKey: ['object-runs', objectId],
    queryFn: () => listObjectRuns(objectId!),
    enabled: !!objectId,
    refetchInterval: live ? 4000 : false,
  });
}

function useInvalidateRuns() {
  const qc = useQueryClient();
  return (objectId?: string) => {
    void qc.invalidateQueries({ queryKey: ['runs'] });
    void qc.invalidateQueries({ queryKey: ['run'] });
    void qc.invalidateQueries({ queryKey: ['object-runs'] });
    void qc.invalidateQueries({ queryKey: ['objects'] });
    void qc.invalidateQueries({ queryKey: ['projects'] });
    if (objectId) void qc.invalidateQueries({ queryKey: ['object', objectId] });
  };
}
export function useIntakeTypes(objectId: string | undefined) {
  return useQuery({ queryKey: ['intake-types', objectId], queryFn: () => listIntakeTypes(objectId!), enabled: !!objectId, staleTime: 60_000 });
}
export function useLaunchRun(objectId: string) {
  const invalidate = useInvalidateRuns();
  return useMutation({ mutationFn: (body: LaunchRunBody) => launchRun(objectId, body), onSuccess: () => invalidate(objectId) });
}
export function useMarkReady(objectId: string) {
  const invalidate = useInvalidateRuns();
  return useMutation({
    mutationFn: () => markReady(objectId),
    onSuccess: () => invalidate(objectId),
    onError: (err) => notifications.show({ color: 'red', message: errorMessage(err) }),
  });
}
export function useCancelRun() {
  const invalidate = useInvalidateRuns();
  return useMutation({ mutationFn: cancelRun, onSuccess: (r) => invalidate(r.data_object ?? undefined) });
}
export function useRefreshRun() {
  const invalidate = useInvalidateRuns();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: refreshRun,
    onSuccess: (r) => {
      qc.setQueryData(['run', r.run.public_id], r.run);
      if (r.advanced) invalidate(r.run.data_object ?? undefined);
    },
  });
}
